import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, X } from "lucide-react";
import { api } from "../../lib/api";
import { CONTENT_SCHEMAS } from "./schemas";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const inputCls = "bg-black/30 border-white/10 text-white placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand rounded-lg";

function FieldEditor({ field, value, onChange }) {
  if (field.type === "textarea") {
    return <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} data-testid={`field-${field.name}`} />;
  }
  if (field.type === "bool") {
    return (
      <div className="flex items-center gap-3">
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${field.name}`} />
        <span className="text-sm text-muted-foreground">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className={inputCls} data-testid={`field-${field.name}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="bg-surface-2 border-white/10 text-white">
          {field.options.map((o) => <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "stringlist") {
    const text = Array.isArray(value) ? value.join("\n") : (value || "");
    return <Textarea rows={4} value={text} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trimStart()).filter((s) => s.length))} className={`${inputCls} resize-none`} data-testid={`field-${field.name}`} placeholder="One item per line" />;
  }
  if (field.type === "metriclist") {
    const rows = Array.isArray(value) ? value : [];
    const setRow = (i, key, v) => { const next = rows.map((r, ri) => ri === i ? { ...r, [key]: v } : r); onChange(next); };
    const add = () => onChange([...rows, { value: "", label: "" }]);
    const remove = (i) => onChange(rows.filter((_, ri) => ri !== i));
    return (
      <div className="space-y-2" data-testid={`field-${field.name}`}>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={r.value || ""} onChange={(e) => setRow(i, "value", e.target.value)} placeholder="-63%" className={`${inputCls} w-28`} />
            <Input value={r.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Time to collect" className={`${inputCls} flex-1`} />
            <button type="button" onClick={() => remove(i)} className="p-2 text-muted-foreground hover:text-red-400"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-xs text-brand hover:text-brand-hover">+ Add metric</button>
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${field.name}`} />;
}

export default function ContentPanel({ collection }) {
  const schema = CONTENT_SCHEMAS[collection];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, data} or null for new
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/content/${collection}`);
      setItems(data);
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, [collection]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item.data }); setOpen(true); };

  const save = async () => {
    for (const f of schema.fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/content/${collection}/${editing.id}`, { data: form, visible: editing.visible });
        toast.success(`${schema.singular} updated`);
      } else {
        await api.post(`/admin/content/${collection}`, { data: form, visible: true });
        toast.success(`${schema.singular} added`);
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (item) => {
    try {
      await api.put(`/admin/content/${collection}/${item.id}`, { data: item.data, visible: !item.visible });
      setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, visible: !it.visible } : it));
    } catch { toast.error("Update failed"); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[index], b = items[target];
    try {
      await Promise.all([
        api.put(`/admin/content/${collection}/${a.id}`, { data: a.data, visible: a.visible, order: b.order }),
        api.put(`/admin/content/${collection}/${b.id}`, { data: b.data, visible: b.visible, order: a.order }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${schema.singular.toLowerCase()}?`)) return;
    try {
      await api.delete(`/admin/content/${collection}/${item.id}`);
      toast.success("Deleted");
      setItems((prev) => prev.filter((it) => it.id !== item.id));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`panel-${collection}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-medium text-white">{schema.label}</h2>
          <p className="text-sm text-muted-foreground mt-1">{items.length} item{items.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={openNew} data-testid={`add-${collection}`} className="inline-flex items-center gap-2 rounded-xl bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2.5 transition-colors">
          <Plus className="h-4 w-4" /> Add {schema.singular}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-muted-foreground py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-white/10 rounded-2xl">No items yet. Add your first {schema.singular.toLowerCase()}.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={item.id} className={`flex items-center gap-4 rounded-2xl border border-white/5 bg-white/[0.015] p-4 ${!item.visible ? "opacity-50" : ""}`} data-testid={`item-${collection}-${i}`}>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-muted-foreground hover:text-white disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-muted-foreground hover:text-white disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">{item.data[schema.titleField] || "(untitled)"}</div>
                {item.data.draft && <span className="mt-1 inline-block rounded-full bg-brand/15 text-brand text-[10px] font-mono uppercase tracking-wider px-2 py-0.5">Draft</span>}
              </div>
              <button onClick={() => toggleVisible(item)} title={item.visible ? "Visible on site" : "Hidden"} className="p-2 text-muted-foreground hover:text-white" data-testid={`toggle-${collection}-${i}`}>
                {item.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button onClick={() => openEdit(item)} className="p-2 text-muted-foreground hover:text-brand" data-testid={`edit-${collection}-${i}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(item)} className="p-2 text-muted-foreground hover:text-red-400" data-testid={`delete-${collection}-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface-1 border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {schema.singular}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Changes go live on the website immediately after saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {schema.fields.map((f) => (
              <div key={f.name} className="space-y-2">
                <Label className="text-sm text-white/80">{f.label}{f.required && " *"}</Label>
                <FieldEditor field={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-xl bg-white/5 border border-white/10 text-white text-sm px-4 py-2.5 hover:bg-white/10">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-content-btn" className="rounded-xl bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
